/**
 * Data layer for the Sleep section — now backed by the real Sleep Hub admin
 * APIs (Backend: controllers/admin/v1/sleepHubController.js).
 *
 * Every page in container/SleepHub reads through these fetchers. Each fetcher
 * unwraps the standard `{ data, meta: { code } }` response and adapts the
 * payload to the shape the pages consume. Read-only dashboards resolve `null`
 * on failure (pages render placeholders); list fetchers throw so their
 * callers' try/catch shows an empty table.
 */

import { Api } from '../../api';

// Standard success unwrap: `{ data, meta: { code: 1 } }` → data.
// Exported for the company panel's data layer (companySleepApi.js).
export const unwrap = (response) => {
  if (response?.data?.meta?.code === 1) return response.data.data;
  throw new Error(response?.data?.meta?.message || 'Request failed');
};

/**
 * Shared shaping for the sounds dashboards.
 *
 * The super-admin and company endpoints return the same listening block (both
 * come from services/sleepHubService.js#buildSoundsListeningData) — only the
 * library half differs — so both fetchers adapt it through here and the two
 * pages can render the same panels.
 */
const adaptSoundsListening = (data) => ({
  listeners: data.listeners,
  totalPlays: data.totalPlays,
  totalPlayTimeMins: data.totalPlayTimeMins,
  avgSessionMins: data.avgSessionMins,
  avgPlaysPerListener: data.avgPlaysPerListener,
  mostHeardSound: data.mostHeardSound,
  topTenSharePercentage: data.topTenSharePercentage,
  mostPlayed: (data.mostPlayed || []).map((sound) => ({
    id: sound._id,
    name: sound.name,
    plays: sound.plays,
    listeners: sound.listeners ?? null,
    playTimeMins: sound.playTimeMins,
    rating: sound.rating ?? null
  })),
  // Backend zero-fills the last 30 days; charts consume plain series.
  playsTrend: (data.playsTrend || []).map((day) => day.plays),
  listenMinsTrend: (data.playsTrend || []).map((day) => day.listenMins || 0),
  weekdayPlays: data.weekdayPlays || [],
  sessionLengthDistribution: data.sessionLengthDistribution || [],
  listenerEngagement: data.listenerEngagement || []
});

/** Distributions shared by the tracker dashboards (admin + company). */
const adaptTracker = (data) => ({
  totalLogs: data.totalLogs,
  usersTracking: data.usersTracking,
  avgSleepMins: data.avgSleepMins,
  avgSleepScore: data.avgSleepScore,

  // wearableUsers = syncing logs from a device; connectedWearableUsers = has
  // a live integration linked, whether it has synced anything or not.
  wearableUsers: data.wearableUsers,
  connectedWearableUsers: data.connectedWearableUsers ?? 0,
  wearableProviders: data.wearableProviders || [],

  targetMins: data.targetMins ?? null,
  onTargetLogs: data.onTargetLogs ?? 0,
  onTargetPercentage: data.onTargetPercentage ?? 0,
  commonBedtime: data.commonBedtime || null,
  commonWakeTime: data.commonWakeTime || null,

  avgDurationTrend: (data.dailyTrend || []).map((day) => day.avgDurationMins),
  logsPerDay: (data.dailyTrend || []).map((day) => day.logs),
  scoreTrend: (data.dailyTrend || []).map((day) => day.avgScore || 0),

  qualityDistribution: data.qualityDistribution || [],
  deviceSourceSplit: data.deviceSourceSplit || [],
  durationDistribution: data.durationDistribution || [],
  scoreDistribution: data.scoreDistribution || [],
  bedtimeDistribution: data.bedtimeDistribution || [],
  wakeTimeDistribution: data.wakeTimeDistribution || [],
  weekdayPattern: data.weekdayPattern || [],
  trackingConsistency: data.trackingConsistency || []
});

export const fetchSoundsDashboard = async () => {
  try {
    const data = unwrap(await Api.getSleepSoundsDashboard());
    return {
      ...adaptSoundsListening(data),
      // Library half — super admin only; the company panel has no view of the
      // catalogue, only of what its people played.
      totalSounds: data.totalSounds,
      totalPlays: data.totalPlays,
      totalPlayTimeMins: data.totalPlayTimeMins,
      avgRating: data.avgRating,
      ratedSounds: data.ratedSounds ?? 0,
      unplayedSounds: data.unplayedSounds ?? 0,
      unplayedPercentage: data.unplayedPercentage ?? 0,
      ratingDistribution: data.ratingDistribution || [],
      authorSplit: data.authorSplit || []
    };
  } catch (error) {
    console.error('fetchSoundsDashboard error:', error);
    return null;
  }
};

export const fetchTrackerDashboard = async () => {
  try {
    return adaptTracker(unwrap(await Api.getSleepTrackerDashboard()));
  } catch (error) {
    console.error('fetchTrackerDashboard error:', error);
    return null;
  }
};

// Re-exported for the company panel's data layer (companySleepApi.js).
export { adaptTracker, adaptSoundsListening };

export const fetchSleepCompanies = async ({ page = 1, perPage = 10, search = '' } = {}) => {
  const data = unwrap(await Api.getSleepHubCompanies(page, perPage, search));
  return {
    data: (data.data || []).map((company) => ({
      ...company,
      // The table's partner-profile cell reads the logo from company_logo.
      company_logo: company.companyLogo || null
    })),
    totalCount: data.totalCount || 0
  };
};

export const fetchSleepCompanyDetail = async (companyId) => {
  try {
    const data = unwrap(await Api.getSleepHubCompanyDetail(companyId));
    return { company: data.company || null };
  } catch (error) {
    console.error('fetchSleepCompanyDetail error:', error);
    return { company: null };
  }
};

export const fetchSleepUsers = async ({
  page = 1,
  perPage = 10,
  search = '',
  accountType = '', // '' | 'B2C' | 'B2B'
  companyId = ''
} = {}) => {
  const data = unwrap(
    await Api.getSleepHubUsers(page, perPage, search, accountType, companyId)
  );
  return { data: data.data || [], totalCount: data.totalCount || 0 };
};

export const fetchSleepUserDetail = async (userId) => {
  try {
    // Payload shape already matches the page: { user, wearableDevice,
    // mostHeardSound, totalSoundPlays, soundPlayTimeMins, sleepLogs,
    // topSounds, soundHistory }.
    return unwrap(await Api.getSleepHubUserDetail(userId));
  } catch (error) {
    console.error('fetchSleepUserDetail error:', error);
    return null;
  }
};

// Shared formatting helpers -------------------------------------------------

export const minsToHhMm = (mins) => {
  if (mins == null || isNaN(mins)) return '-';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${m}m`;
};

export const formatBigNumber = (value) => {
  if (value == null || isNaN(value)) return '-';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return `${value}`;
};

// Last-N-days date labels, oldest first, e.g. "2 Jul". Matches the backend's
// zero-filled last-N-days trend series.
export const lastNDaysLabels = (n) => {
  const labels = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    labels.push(d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }));
  }
  return labels;
};
